export type Listener = () => void;

export interface Store<T extends object> {
  getSnapshot: () => T;
  subscribe: (listener: Listener) => () => void;
  setState: (patch: Partial<T> | ((prev: T) => Partial<T>)) => void;
}

/**
 * A minimal external store, shaped for `useSyncExternalStore`.
 *
 * The media engine lives outside React entirely (it owns encoders, audio graphs
 * and canvases). React reads it through this store, so state flows one way:
 * engine -> store -> render. No `useEffect` choreography required.
 */
export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<Listener>();

  return {
    getSnapshot: () => state,
    subscribe: (listener: Listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setState: (patch) => {
      const next = typeof patch === 'function' ? patch(state) : patch;
      let changed = false;
      for (const key of Object.keys(next) as Array<keyof T>) {
        if (!Object.is(state[key], next[key])) {
          changed = true;
          break;
        }
      }
      if (!changed) return;
      state = { ...state, ...next };
      for (const listener of Array.from(listeners)) listener();
    },
  };
}
